import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {AnimatePresence, motion, useReducedMotion} from "framer-motion";
import {object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";

import {validationMessages} from "../../../form/utils/validation_messages";
import {getCheckboxConfigBySource} from "../../constants/lead/lead_modal_checkbox_options";
import {ApplicationSource} from "../../utils/form/application_form_source";
import {normalizeLeadQuestions} from "../../utils/lead/normalize_lead_questions";
import {LeadButtonTerms} from "../LeadButtonTerms";
import {LeadRodoTerms} from "../LeadRodoTerms";

interface IAskFormValues {
    questions: string[];
    userMessage: string;
}

interface IProps {
    isOpen: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    onSelectedQuestionsChange?: (questions: string[]) => void;
    onSubmit: (additionalQuestions: {value: string; label: string}[]) => void;
    vendorLogoUrl: string | null;
    vendorName: string;
}

const validationSchema = object({
    userMessage: string().when("questions", {
        is: (questions: string[]) => Array.isArray(questions) && questions.some((question) => question === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

const checkboxConfig = getCheckboxConfigBySource(ApplicationSource.RecommendedProperty);
const allOptions = [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions];

type IFormProps = Pick<IProps, "isSubmitting" | "onSelectedQuestionsChange" | "onSubmit" | "vendorLogoUrl" | "vendorName">;

function TinderMultiLeadAskForm({isSubmitting, onSelectedQuestionsChange, onSubmit, vendorLogoUrl, vendorName}: IFormProps) {
    const [showMoreOptions, setShowMoreOptions] = useState(false);

    const formMethods = useForm<IAskFormValues>({
        resolver: yupResolver(validationSchema) as never,
        mode: "onChange",
        defaultValues: {questions: [], userMessage: ""}
    });

    const selectedQuestions = formMethods.watch("questions") || [];

    useEffect(() => {
        onSelectedQuestionsChange?.(selectedQuestions);
    }, [selectedQuestions, onSelectedQuestionsChange]);
    const shouldShowAllOptions = checkboxConfig.showAllOptions || showMoreOptions;
    const otherOptionSelected = selectedQuestions.some((question) => question === "inny");
    const combinedOptions = shouldShowAllOptions ? allOptions : checkboxConfig.baseOptions;
    const rodoTermsUrl = rpAppLink.rodoTerms.base();

    const handleValidSubmit = (values: IAskFormValues) => {
        onSubmit(normalizeLeadQuestions(allOptions, values.questions || [], values.userMessage));
    };

    return (
        <FormProvider {...formMethods}>
            <form className={formStyle} onSubmit={formMethods.handleSubmit(handleValidSubmit)}>
                <div className={scrollStyle}>
                    <div className={headerStyle}>
                        <div className={headerRowStyle}>
                            {vendorLogoUrl ? (
                                <img className={logoStyle} src={vendorLogoUrl} alt={vendorName} width={72} height={54} />
                            ) : (
                                <span className={logoPlaceholderStyle} aria-hidden />
                            )}
                            <Text variant="body_copy_2" color="var(--colors-secondary)">
                                Wysyłasz zapytanie do dewelopera {vendorName}
                            </Text>
                        </div>
                        <span className={dividerStyle} aria-hidden />
                    </div>

                    <Text variant="headline_6" as="h2" color="var(--colors-secondary)" className={questionsLabelStyle}>
                        Chcę zapytać o:
                    </Text>

                    <CheckBoxGroupControlled
                        name="questions"
                        options={combinedOptions}
                        checkBoxClassName={checkboxItemStyle}
                        checkboxListClassName={checkboxGridStyle}
                        checkedMarkColor="#02D054"
                    />

                    {!checkboxConfig.showAllOptions ? (
                        <Text className={showMoreOptionsStyle} variant="info_txt_1" strong onClick={() => setShowMoreOptions(!showMoreOptions)}>
                            {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                        </Text>
                    ) : null}

                    {shouldShowAllOptions && otherOptionSelected ? (
                        <TextareaField className={textareaStyle} name="userMessage" placeholder="Wpisz własną wiadomość do dewelopera." />
                    ) : null}
                </div>

                <div className={footerStyle}>
                    <Button variant="filled_primary" fullWidth type="submit" isLoading={isSubmitting} disabled={isSubmitting} className={submitButtonStyle}>
                        Wyślij
                    </Button>

                    <div className={termsStyle}>
                        <LeadButtonTerms as="p" buttonText="Wyślij" color="initial" mb={1} />
                        <LeadRodoTerms
                            as="p"
                            color="initial"
                            linkClassName={rodoBoldLinkStyle}
                            hiddenInfo={{
                                linkTexts: ["Więcej", "Mniej"],
                                infoElement: (
                                    <Text as="span" variant="info_txt_3" color="initial">
                                        <Link href={rodoTermsUrl} target="_blank" textClassName={rodoLinkStyle}>
                                            dowiedz się więcej o przetwarzaniu Twoich danych
                                        </Link>
                                        {vendorName ? <>, {vendorName}</> : null}
                                    </Text>
                                )
                            }}
                        />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

export function TinderMultiLeadAskSheet({isOpen, isSubmitting, onClose, onSelectedQuestionsChange, onSubmit, vendorLogoUrl, vendorName}: IProps) {
    const shouldReduceMotion = useReducedMotion();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!mounted) {
        return null;
    }

    return createPortal(
        <AnimatePresence>
            {isOpen ? (
                <motion.div key="tinder-ask-sheet" className={rootStyle}>
                    <motion.div
                        className={overlayStyle}
                        onClick={onClose}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: shouldReduceMotion ? 0.15 : 0.25, ease: [0.4, 0, 0.2, 1]}}
                    />

                    <motion.div
                        className={sheetStyle}
                        role="dialog"
                        aria-modal
                        aria-label={`Wyślij zapytanie do dewelopera ${vendorName}`}
                        initial={shouldReduceMotion ? {opacity: 0} : {y: "100%"}}
                        animate={shouldReduceMotion ? {opacity: 1} : {y: 0}}
                        exit={shouldReduceMotion ? {opacity: 0} : {y: "100%"}}
                        transition={{duration: shouldReduceMotion ? 0.18 : 0.34, ease: [0.4, 0, 0.2, 1]}}
                    >
                        <button type="button" className={closeButtonStyle} onClick={onClose} aria-label="Zamknij">
                            <CloseIcon size="1.6" wrapperSize="1.6" fill="currentColor" />
                        </button>

                        <TinderMultiLeadAskForm
                            isSubmitting={isSubmitting}
                            onSelectedQuestionsChange={onSelectedQuestionsChange}
                            onSubmit={onSubmit}
                            vendorLogoUrl={vendorLogoUrl}
                            vendorName={vendorName}
                        />
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>,
        document.body
    );
}

const rootStyle = css({
    position: "fixed",
    inset: 0,
    // Stack above the lead modal (leadModalZindex === 10001).
    zIndex: 10002,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center"
});

const overlayStyle = css({
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)"
});

const sheetStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxHeight: "90dvh",
    overflow: "hidden",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    backgroundColor: "var(--colors-white)",
    willChange: "transform",
    md: {
        // 95% of the lead modal width (modalContentStyle), leaving a delicate margin on each side.
        width: "calc(min(985px, 100vw - 32px) * 0.95)"
    }
});

const closeButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 1.5,
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    border: 0,
    borderRadius: "100%",
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    cursor: "pointer"
});

const formStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0
});

const scrollStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflowY: "auto",
    px: 1.5,
    pt: 1,
    pb: 1,
    md: {
        px: 3
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    mb: 2
});

const headerRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    pr: 4
});

const logoStyle = css({
    flexShrink: 0,
    width: "72px",
    height: "54px",
    objectFit: "cover"
});

const logoPlaceholderStyle = css({
    flexShrink: 0,
    width: "72px",
    height: "54px",
    borderRadius: 1,
    backgroundColor: "var(--colors-gray-200)"
});

const dividerStyle = css({
    width: "100%",
    height: "1px",
    backgroundColor: "var(--colors-gray-300)"
});

const questionsLabelStyle = css({
    mb: 1.5
});

const checkboxItemStyle = css({
    alignItems: "flex-start"
});

const checkboxGridStyle = css({
    md: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "0.5rem 1rem"
    }
});

const showMoreOptionsStyle = css({
    mt: 1,
    textDecoration: "underline",
    cursor: "pointer"
});

const textareaStyle = css({
    mt: 2
});

const footerStyle = css({
    flexShrink: 0,
    px: 1.5,
    pt: 1,
    pb: 1.5,
    backgroundColor: "var(--colors-white)",
    md: {
        px: 3,
        pb: 2
    }
});

const submitButtonStyle = css({
    minHeight: "56px",
    textTransform: "uppercase"
});

const termsStyle = css({
    mt: 1.5
});

const rodoBoldLinkStyle = css({
    fontWeight: "bold"
});

const rodoLinkStyle = css({
    fontSize: "inherit",
    lineHeight: "inherit",
    textDecoration: "underline"
});
import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {AnimatePresence, motion, useReducedMotion} from "framer-motion";
import {object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";

import {validationMessages} from "../../../form/utils/validation_messages";
import {getCheckboxConfigBySource} from "../../constants/lead/lead_modal_checkbox_options";
import {ApplicationSource} from "../../utils/form/application_form_source";
import {normalizeLeadQuestions} from "../../utils/lead/normalize_lead_questions";
import {LeadButtonTerms} from "../LeadButtonTerms";
import {LeadRodoTerms} from "../LeadRodoTerms";

interface IAskFormValues {
    questions: string[];
    userMessage: string;
}

interface IProps {
    isOpen: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    onSelectedQuestionsChange?: (questions: string[]) => void;
    onSubmit: (additionalQuestions: {value: string; label: string}[]) => void;
    vendorLogoUrl: string | null;
    vendorName: string;
}

const validationSchema = object({
    userMessage: string().when("questions", {
        is: (questions: string[]) => Array.isArray(questions) && questions.some((question) => question === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

const checkboxConfig = getCheckboxConfigBySource(ApplicationSource.RecommendedProperty);
const allOptions = [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions];

type IFormProps = Pick<IProps, "isSubmitting" | "onSelectedQuestionsChange" | "onSubmit" | "vendorLogoUrl" | "vendorName">;

function TinderMultiLeadAskForm({isSubmitting, onSelectedQuestionsChange, onSubmit, vendorLogoUrl, vendorName}: IFormProps) {
    const [showMoreOptions, setShowMoreOptions] = useState(false);

    const formMethods = useForm<IAskFormValues>({
        resolver: yupResolver(validationSchema) as never,
        mode: "onChange",
        defaultValues: {questions: [], userMessage: ""}
    });

    const selectedQuestions = formMethods.watch("questions") || [];

    useEffect(() => {
        onSelectedQuestionsChange?.(selectedQuestions);
    }, [selectedQuestions, onSelectedQuestionsChange]);
    const shouldShowAllOptions = checkboxConfig.showAllOptions || showMoreOptions;
    const otherOptionSelected = selectedQuestions.some((question) => question === "inny");
    const combinedOptions = shouldShowAllOptions ? allOptions : checkboxConfig.baseOptions;
    const rodoTermsUrl = rpAppLink.rodoTerms.base();

    const handleValidSubmit = (values: IAskFormValues) => {
        onSubmit(normalizeLeadQuestions(allOptions, values.questions || [], values.userMessage));
    };

    return (
        <FormProvider {...formMethods}>
            <form className={formStyle} onSubmit={formMethods.handleSubmit(handleValidSubmit)}>
                <div className={scrollStyle}>
                    <div className={headerStyle}>
                        <div className={headerRowStyle}>
                            {vendorLogoUrl ? (
                                <img className={logoStyle} src={vendorLogoUrl} alt={vendorName} width={72} height={54} />
                            ) : (
                                <span className={logoPlaceholderStyle} aria-hidden />
                            )}
                            <Text variant="body_copy_2" color="var(--colors-secondary)">
                                Wysyłasz zapytanie do dewelopera {vendorName}
                            </Text>
                        </div>
                        <span className={dividerStyle} aria-hidden />
                    </div>

                    <Text variant="headline_6" as="h2" color="var(--colors-secondary)" className={questionsLabelStyle}>
                        Chcę zapytać o:
                    </Text>

                    <CheckBoxGroupControlled
                        name="questions"
                        options={combinedOptions}
                        checkBoxClassName={checkboxItemStyle}
                        checkboxListClassName={checkboxGridStyle}
                        checkedMarkColor="#02D054"
                    />

                    {!checkboxConfig.showAllOptions ? (
                        <Text className={showMoreOptionsStyle} variant="info_txt_1" strong onClick={() => setShowMoreOptions(!showMoreOptions)}>
                            {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                        </Text>
                    ) : null}

                    {shouldShowAllOptions && otherOptionSelected ? (
                        <TextareaField className={textareaStyle} name="userMessage" placeholder="Wpisz własną wiadomość do dewelopera." />
                    ) : null}
                </div>

                <div className={footerStyle}>
                    <Button variant="filled_primary" fullWidth type="submit" isLoading={isSubmitting} disabled={isSubmitting} className={submitButtonStyle}>
                        Wyślij
                    </Button>

                    <div className={termsStyle}>
                        <LeadButtonTerms as="p" buttonText="Wyślij" color="initial" mb={1} />
                        <LeadRodoTerms
                            as="p"
                            color="initial"
                            linkClassName={rodoBoldLinkStyle}
                            hiddenInfo={{
                                linkTexts: ["Więcej", "Mniej"],
                                infoElement: (
                                    <Text as="span" variant="info_txt_3" color="initial">
                                        <Link href={rodoTermsUrl} target="_blank" textClassName={rodoLinkStyle}>
                                            dowiedz się więcej o przetwarzaniu Twoich danych
                                        </Link>
                                        {vendorName ? <>, {vendorName}</> : null}
                                    </Text>
                                )
                            }}
                        />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

export function TinderMultiLeadAskSheet({isOpen, isSubmitting, onClose, onSelectedQuestionsChange, onSubmit, vendorLogoUrl, vendorName}: IProps) {
    const shouldReduceMotion = useReducedMotion();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!mounted) {
        return null;
    }

    return createPortal(
        <AnimatePresence>
            {isOpen ? (
                <motion.div key="tinder-ask-sheet" className={rootStyle}>
                    <motion.div
                        className={overlayStyle}
                        onClick={onClose}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: shouldReduceMotion ? 0.15 : 0.25, ease: [0.4, 0, 0.2, 1]}}
                    />

                    <motion.div
                        className={sheetStyle}
                        role="dialog"
                        aria-modal
                        aria-label={`Wyślij zapytanie do dewelopera ${vendorName}`}
                        initial={shouldReduceMotion ? {opacity: 0} : {y: "100%"}}
                        animate={shouldReduceMotion ? {opacity: 1} : {y: 0}}
                        exit={shouldReduceMotion ? {opacity: 0} : {y: "100%"}}
                        transition={{duration: shouldReduceMotion ? 0.18 : 0.34, ease: [0.4, 0, 0.2, 1]}}
                    >
                        <button type="button" className={closeButtonStyle} onClick={onClose} aria-label="Zamknij">
                            <CloseIcon size="1.6" wrapperSize="1.6" fill="currentColor" />
                        </button>

                        <TinderMultiLeadAskForm
                            isSubmitting={isSubmitting}
                            onSelectedQuestionsChange={onSelectedQuestionsChange}
                            onSubmit={onSubmit}
                            vendorLogoUrl={vendorLogoUrl}
                            vendorName={vendorName}
                        />
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>,
        document.body
    );
}

const rootStyle = css({
    position: "fixed",
    inset: 0,
    // Stack above the lead modal (leadModalZindex === 10001).
    zIndex: 10002,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center"
});

const overlayStyle = css({
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)"
});

const sheetStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxHeight: "90dvh",
    overflow: "hidden",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    backgroundColor: "var(--colors-white)",
    willChange: "transform",
    md: {
        // 95% of the lead modal width (modalContentStyle), leaving a delicate margin on each side.
        width: "calc(min(985px, 100vw - 32px) * 0.95)"
    }
});

const closeButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 1.5,
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    border: 0,
    borderRadius: "100%",
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    cursor: "pointer"
});

const formStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0
});

const scrollStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflowY: "auto",
    px: 1.5,
    pt: 1,
    pb: 1,
    md: {
        px: 3
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    mb: 2
});

const headerRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    pr: 4
});

const logoStyle = css({
    flexShrink: 0,
    width: "72px",
    height: "54px",
    objectFit: "cover"
});

const logoPlaceholderStyle = css({
    flexShrink: 0,
    width: "72px",
    height: "54px",
    borderRadius: 1,
    backgroundColor: "var(--colors-gray-200)"
});

const dividerStyle = css({
    width: "100%",
    height: "1px",
    backgroundColor: "var(--colors-gray-300)"
});

const questionsLabelStyle = css({
    mb: 1.5
});

const checkboxItemStyle = css({
    alignItems: "flex-start"
});

const checkboxGridStyle = css({
    md: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "0.5rem 1rem"
    }
});

const showMoreOptionsStyle = css({
    mt: 1,
    textDecoration: "underline",
    cursor: "pointer"
});

const textareaStyle = css({
    mt: 2
});

const footerStyle = css({
    flexShrink: 0,
    px: 1.5,
    pt: 1,
    pb: 1.5,
    backgroundColor: "var(--colors-white)",
    md: {
        px: 3,
        pb: 2
    }
});

const submitButtonStyle = css({
    minHeight: "56px",
    textTransform: "uppercase"
});

const termsStyle = css({
    mt: 1.5
});

const rodoBoldLinkStyle = css({
    fontWeight: "bold"
});

const rodoLinkStyle = css({
    fontSize: "inherit",
    lineHeight: "inherit",
    textDecoration: "underline"
});
